import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { EMPTY_PROFILE } from "@/lib/constants";

const ProfileContext = createContext(null);
const STORAGE_KEY = "correl_profile";
const RESULT_KEY = "correl_result";
const STREAM_RESULT_KEY = "correl_stream_result";

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...EMPTY_PROFILE, ...JSON.parse(saved) } : EMPTY_PROFILE;
    } catch {
      return EMPTY_PROFILE;
    }
  });

  const [result, setResult] = useState(() => {
    try {
      const saved = localStorage.getItem(RESULT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [streamResult, setStreamResult] = useState(() => {
    try {
      const saved = localStorage.getItem(STREAM_RESULT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  }, [profile]);

  useEffect(() => {
    if (result) localStorage.setItem(RESULT_KEY, JSON.stringify(result));
  }, [result]);

  useEffect(() => {
    if (streamResult) localStorage.setItem(STREAM_RESULT_KEY, JSON.stringify(streamResult));
  }, [streamResult]);

  const updateProfile = useCallback((patch) => setProfile((p) => ({ ...p, ...patch })), []);
  const resetProfile = useCallback(() => {
    setProfile(EMPTY_PROFILE);
    setResult(null);
    setStreamResult(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(RESULT_KEY);
    localStorage.removeItem(STREAM_RESULT_KEY);
  }, []);

  const value = useMemo(
    () => ({ profile, setProfile, updateProfile, resetProfile, result, setResult, streamResult, setStreamResult }),
    [profile, updateProfile, resetProfile, result, streamResult],
  );

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
}
